import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { api } from "../lib/api";
import { Paperclip, Upload, X as XIcon, FileIcon } from "lucide-react";
import { toast } from "sonner";

function Field({ field, value, onChange }) {
  const testid = `form-field-${field.key}`;
  if (field.type === "textarea") {
    return (
      <Textarea
        id={field.key}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid={testid}
      />
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger id={field.key} data-testid={testid}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {(field.options || []).map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "boolean") {
    return (
      <div className="flex items-center gap-3 h-10">
        <Switch
          id={field.key}
          checked={!!value}
          onCheckedChange={onChange}
          data-testid={testid}
        />
        <span className="text-sm text-slate-600">{value ? "Yes" : "No"}</span>
      </div>
    );
  }
  return (
    <Input
      id={field.key}
      type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  );
}

export default function EntityFormDialog({
  open, onOpenChange, schema, initial, onSubmit, title,
}) {
  const [form, setForm] = useState(() => ({
    title: initial?.title ?? "",
    data: { ...(initial?.data ?? {}) },
    remarks: initial?.remarks ?? "",
    attachments: initial?.attachments ?? [],
  }));
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const entityId = initial?.id;
  const isEdit = !!entityId;

  useEffect(() => {
    if (!open) return;
    setForm({
      title: initial?.title ?? "",
      data: { ...(initial?.data ?? {}) },
      remarks: initial?.remarks ?? "",
      attachments: initial?.attachments ?? [],
    });
    if (isEdit && initial?.attachments?.length) {
      api.get("/files").then(({ data }) => {
        setFiles(data.filter((f) => initial.attachments.includes(f.id)));
      }).catch(() => {});
    } else {
      setFiles([]);
    }
  }, [open, initial, isEdit]);

  const update = (k, v) =>
    setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        title: form.title,
        data: form.data,
        remarks: form.remarks,
        attachments: form.attachments,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const uploadNow = async (file) => {
    if (!entityId) {
      toast.error("Save this entry first, then attach files");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_id", entityId);
      fd.append("holder_name", form.data?.holder_name || "");
      const { data: rec } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), rec.id] }));
      setFiles((list) => [...list, rec]);
      toast.success("Attached");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const detach = async (fileId) => {
    setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((id) => id !== fileId) }));
    setFiles((list) => list.filter((f) => f.id !== fileId));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="form-field-title"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {schema.fields.map((field) => (
              <div key={field.key} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label htmlFor={field.key}>
                  {field.label}
                  {field.sensitive && (
                    <span className="ml-1 text-[10px] text-amber-600 font-medium">
                      ENCRYPTED
                    </span>
                  )}
                </Label>
                <Field
                  field={field}
                  value={form.data[field.key]}
                  onChange={(v) => update(field.key, v)}
                />
              </div>
            ))}
          </div>
          <div>
            <Label htmlFor="remarks">Remarks / Notes</Label>
            <Textarea
              id="remarks"
              rows={2}
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              data-testid="form-field-remarks"
            />
          </div>

          {/* Attachments */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2">
                <Paperclip className="h-3.5 w-3.5" /> Attached Documents
              </Label>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadNow(e.target.files[0])}
                data-testid="form-attach-input"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={!isEdit || uploading}
                data-testid="form-attach-button"
              >
                <Upload className="h-3.5 w-3.5 mr-1" />
                {uploading ? "Uploading..." : "Attach file"}
              </Button>
            </div>
            {!isEdit && (
              <div className="text-xs text-slate-500">
                Save this entry first — then you can attach PDFs, scans, photos.
              </div>
            )}
            {files.length > 0 && (
              <ul className="space-y-1.5 mt-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs">
                    <FileIcon className="h-3.5 w-3.5 text-slate-600 shrink-0" />
                    <span className="truncate flex-1">{f.filename}</span>
                    <span className="text-slate-400 tabular-nums">{Math.round((f.size || 0) / 1024)} KB</span>
                    <button
                      type="button"
                      onClick={() => detach(f.id)}
                      className="text-slate-500 hover:text-red-600"
                      data-testid={`form-detach-${f.id}`}
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={saving}
              data-testid="form-submit-button">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
